<script setup>
import { ref, onMounted, watch, onBeforeUnmount } from "vue";
import { TilopayClient } from "@/services/TiloPayClient";
import { CoursesClient } from "@/services/CoursesClient";

const tilopayClient = new TilopayClient();
const coursesClient = new CoursesClient();

const headers = ["Estudiante", "Email", "Teléfono", "Fecha"];
const payments = ref([]);
const loading = ref(false);
const totalItems = ref(0);
const netValue = ref(0);
const currency = ref("USD"); 

const studentCount = ref(0);
const dataTable = ref(null);

const categoryOptions = ref([]);
const courseId = ref(null); // Initialize as null

const fetchInstructorCourses = async () => {
  try {
    loading.value = true;
    const response = await coursesClient.getInstructorCoursesById();
    
    if (response?.length) {
      categoryOptions.value = response.map(course => ({
        value: course.id,
        text: course.titulo || course.name || 'Curso sin nombre',
        description: course.description || ''
      }));
      
      if (categoryOptions.value.length > 0) {        courseId.value = categoryOptions.value[0]?.value;
      }
    }
  } catch (err) {
    console.error("Error loading courses:", err);
  } finally {
    loading.value = false;
  }
};

const fetchPayments = async () => {
  if (!courseId.value) return; // Don't fetch if no courseId is set
  
  try {
    loading.value = true;  
    
    const response = await tilopayClient.paymentsByCourse(courseId.value);    
    
    if (response?.students) {
      payments.value = response.students.map(student => ({
        user: student.name || 'N/A',
        email: student.email || 'N/A',
        phone: student.phone || 'N/A',
        date: formatDate(student.fechapago)
      }));
      
      totalItems.value = response.total_students;
      studentCount.value = totalItems.value;
      netValue.value = response.net_value || 0;
      currency.value = response.currency;

    }
  } catch (err) {
    console.error("Error loading students:", err);
  } finally {
    loading.value = false;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Watch for course selection changes
watch(courseId, (newVal) => {
  if (newVal) {
    fetchPayments();
  }
}, { immediate: false }); 

onMounted(async () => {
  await fetchInstructorCourses(); 
});

onBeforeUnmount(() => {
  if (dataTable.value) {
    dataTable.value.destroy();
  }
});
</script>

<template>
  <div class="card">
    <div class="p-3 pb-0 card-header">
      <div class="d-flex justify-content-between">
        <h6 class="mb-2"><i class="fas fa-credit-card pe-2"></i>Historial de Ventas</h6>
        
        <div v-if="loading" class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Cargando...</span>
        </div>
      </div>
    </div>

    <div class="table-responsive pb-4">
      <div class="row d-flex justify-content-between align-items-center px-md-4 px-2 py-2 py-md-4">
        <div class="col-12 col-md-6 d-flex align-items-center">
          <label class="m-0 me-2">Curso</label>
          <div class="position-relative flex-grow-1">
            <select 
              v-model="courseId" 
              class="form-control pe-4 py-1" 
              required
              style="appearance: none; -webkit-appearance: none;"
            >
              <option value="">Seleccione un Curso</option>
              <option 
                v-for="course in categoryOptions" 
                :key="course.value" 
                :value="course.value"
              >
                {{ course.text }}
              </option>
            </select>
            <div class="position-absolute end-0 top-50 translate-middle-y pe-3">
              <i class="fas fa-chevron-down text-secondary"></i>
            </div>
          </div>
        </div>

        <div class="col-6 col-md-3 d-flex align-items-center justify-content-center justify-content-md-end my-3 my-md-0">
          <p class="small mb-0 pe-2">Estudiantes</p>
          <span class="badge bg-info text-white">
            {{ studentCount }}
          </span>
        </div>
        <div class="col-6 col-md-3 d-flex align-items-center justify-content-center justify-content-md-end my-3 my-md-0">
          <p class="small mb-0 pe-2">Valor Bruto</p>
          <span class="badge bg-success text-white">
            {{ currency === 'USD' ? '$' : (currency === 'CRC' ? '₡' : '') }} {{ netValue }}
          </span>
        </div>
      </div>
      <table id="payments-table" class="table align-items-center mb-0 px-4">
        <thead>
          <tr>
            <th v-for="(header, index) in headers" :key="index"
              class="text-uppercase text-dark text-xxs font-weight-bolder opacity-7 px-2 py-3">
              {{ header }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(payment, index) in payments" :key="index">
            <td class="px-2 py-3 text-xs">{{ payment.user }}</td>
            <td class="px-2 py-3 text-xs">{{ payment.email }}</td>
            <td class="px-2 py-3 text-xs">{{ payment.phone }}</td>
            <td class="px-2 py-3 text-xs">{{ payment.date }}</td>
          </tr>
          <tr v-if="payments.length === 0 && !loading">
            <td :colspan="headers.length" class="text-center py-4">No se encontraron registros de ventas en este curso</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.row-hover {
  transition: background-color 0.3s ease;
  position: relative;
}

.row-hover:hover {
  background-color: #f0f4f8;
}

.dataTable-input,
::v-deep(.dataTable-input) {
  width: 500px !important;
  max-width: 100%;
}

.text-xxs {
  font-size: 0.7rem !important;
}


.cell-content {
  min-width: 100%;
  display: inline-block;
}

/* Ensure the table itself doesn't overflow the card */
.table-responsive {
  overflow-x: auto;
}

</style>